import { useUserZodiac } from "@/features/webapp"
import { zodiacIcons } from "@/features/advisors"
import { getZodiacSign, useUserProfile } from "@/features/user"
import { useQuizStore } from "@/stores/quiz-store"

import GeneralBackground from "@/assets/img/profile/general_background.webp"
import SignBack from "@/assets/img/profile/profile_sign_back_image.webp"
import ChevronRight from "@/assets/img/profile/chevron_right.svg?react"

import {
  getElement,
  getMatchSign,
  getModality,
  getMoonSign,
  getPolarity,
  getRulingPlanet,
} from "../config/astro-facts"

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

/** Parse a `YYYY-MM-DD` quiz answer into a UTC-midnight date. */
function parseBirthDate(dob: string | undefined): Date | null {
  if (!dob) return null
  const [y, m, d] = dob.split("-").map(Number)
  if (!y || !m || !d) return null
  return new Date(Date.UTC(y, m - 1, d))
}

interface ProfileHeroProps {
  onEdit: () => void
}

/** Profile hero: zodiac portrait + astrology facts. Ported from ProfileV4 Hero. */
export function ProfileHero({ onEdit }: ProfileHeroProps) {
  const quizZodiac = useUserZodiac()
  const { profile } = useUserProfile()
  const quizDob = useQuizStore((s) => s.answers.dob)
  const dob = profile?.birthDate || (typeof quizDob === "string" ? quizDob : undefined)
  const birth = parseBirthDate(dob)
  const zodiac = (dob && getZodiacSign(dob)) || quizZodiac

  const SignIcon = zodiacIcons[zodiac]
  const element = getElement(zodiac)
  const polarity = getPolarity(zodiac)
  const modality = getModality(zodiac)
  const planet = getRulingPlanet(zodiac)
  const matchSign = getMatchSign(zodiac)
  const moonSign = birth ? getMoonSign(birth) : null
  const MoonIcon = moonSign ? zodiacIcons[moonSign] : null
  const MatchIcon = zodiacIcons[matchSign]

  const bornText = birth
    ? `Born on ${MONTHS[birth.getUTCMonth()]} ${birth.getUTCDate()}, ${birth.getUTCFullYear()}`
    : null

  return (
    <div
      className="flex w-full flex-col items-center bg-contain bg-top bg-no-repeat"
      style={{ backgroundImage: `url(${GeneralBackground})` }}
    >
      <h2 className="py-[13px] text-center font-display text-[22px] font-medium leading-[30px] text-purple-light">
        Profile
      </h2>

      <div className="relative flex items-center justify-center">
        <img src={SignBack} alt="" className="select-none" />
        <span className="absolute text-white [&_svg]:size-[72px]">
          <SignIcon />
        </span>
      </div>

      {bornText && (
        <p className="mt-2 text-center font-body text-sm text-purple-light/70">{bornText}</p>
      )}
      <p className="mb-6 mt-1 text-center font-display text-2xl font-medium text-purple-light">
        Sun Sign - {zodiac}
      </p>

      <div className="flex w-full max-w-[380px] flex-wrap justify-center gap-4 px-4">
        <ImageStat title="Element" image={element.image} value={element.title} />
        {moonSign && MoonIcon && (
          <IconStat title="Moon Sign" value={moonSign}>
            <MoonIcon />
          </IconStat>
        )}
        <ImageStat title="Polarity" image={polarity.image} value={polarity.title} />
        <ImageStat title="Modality" image={modality.image} value={modality.title} />
        <ImageStat title="Ruling Planet" image={planet.image} value={planet.title} />
        <IconStat title="Zodiac Match" value={matchSign}>
          <MatchIcon />
        </IconStat>
      </div>

      <button
        type="button"
        onClick={onEdit}
        className="mt-6 flex h-11 items-center justify-center gap-2 rounded-full border border-purple-light/20 px-4 font-body text-base font-semibold text-white transition-colors hover:border-purple-light/40 [&_svg]:w-[7px]"
      >
        Edit my profile
        <ChevronRight />
      </button>
    </div>
  )
}

function StatShell({
  title,
  value,
  children,
}: {
  title: string
  value: string
  children: React.ReactNode
}) {
  return (
    <div className="flex w-[calc((100%-2rem)/3)] shrink-0 flex-col items-center justify-center">
      <div className="flex size-[72px] items-center justify-center rounded-full bg-gradient-to-b from-purple-light/40 to-main-purple/20">
        {children}
      </div>
      <span className="mt-1 text-center font-body text-sm text-purple-light/70">{title}</span>
      <span className="text-center font-display text-base font-medium text-purple-light">
        {value}
      </span>
    </div>
  )
}

function ImageStat({ title, image, value }: { title: string; image: string; value: string }) {
  return (
    <StatShell title={title} value={value}>
      <img src={image} alt="" className="size-9" />
    </StatShell>
  )
}

function IconStat({
  title,
  value,
  children,
}: {
  title: string
  value: string
  children: React.ReactNode
}) {
  return (
    <StatShell title={title} value={value}>
      <span className="text-purple-light [&_svg]:size-9">{children}</span>
    </StatShell>
  )
}
